import { omit, pick } from "ramda"

import constants from "common/consts/config"
import cuckoo from "common/services/cuckoo/cuckoo"
import { cuckooEvents } from "common/consts/enums"
import Options from "common/records/Options"
import { makeQuery } from "common/services/query/queries"
import { getAffilidAccordingToPriority } from "common/services/affilId/tools"
import { FEATURE_NAMES } from "common/consts/search"
import createSearchParams from "./createSearchParams"

const commonSearchAPIHeaders = {
  "X-Application": typeof window === "undefined" ? "node" : "frontend",
}

const searchIdExcludedParams = {
  kayak: ["device"],
  salesman: [
    "daysInDestinationFrom",
    "daysInDestinationTo",
    "typeFlight",
    "featureName",
    "wait_for_refresh",
    "one_per_date",
  ],
}

const pickMulticityParams = pick([
  "price_from",
  "price_to",
  "adults",
  "children",
  "infants",
  "sort",
  "asc",
  "affilid",
])

export function prepareSearch(request, options: Options) {
  const apiDomainUrl = options.apiUrl || constants.apiUrl
  const umbrellaUrl = options.umbrellaUrl || constants.umbrellaUrl

  let skypickerApiUrl
  if (request.format === "prices" || request.featureName === "calendar") {
    skypickerApiUrl = `${apiDomainUrl}/aggregation_flights`
  } else if (request.multicity) {
    skypickerApiUrl = `${apiDomainUrl}/flights/multicity`
  } else if (request.salesman) {
    skypickerApiUrl = `${apiDomainUrl}/traveling_salesman`
  } else if (request.featureName === "kayak") {
    skypickerApiUrl = options.metaApiUrl || constants.metaApiUrl
  } else {
    skypickerApiUrl = `${apiDomainUrl}/flights`
  }

  const defaultSearchParams = {
    v: 3, // Locations API is v3
    sort: "price",
    asc: 1,
    locale: options.language,
    daysInDestinationFrom: "",
    daysInDestinationTo: "",
    affilid: getAffilidAccordingToPriority(options.affiliateThisSession, options.affiliate),
    children: 0,
    infants: 0,
  }

  if (options.branding.get("partner")) {
    defaultSearchParams.partner = options.branding.get("partner")
  }

  let searchId
  let searchParams
  if (request.multicity) {
    searchParams = {
      multicity: request.multicity.map(singleRequest => {
        const mergedRequest = { ...singleRequest, ...request }
        return { ...defaultSearchParams, ...createSearchParams(mergedRequest) }
      }),
    }
    searchId = request.multicity
      .map(singleRequest => {
        const mergedRequest = { ...singleRequest, ...request }
        const params = { ...searchParams, ...createSearchParams(mergedRequest) }
        return Object.keys(params)
          .filter(
            key =>
              !searchIdExcludedParams[request.featureName] ||
              !searchIdExcludedParams[request.featureName].includes(key),
          )
          .map(key => `${key}=${params[key]}`)
          .join("&")
      })
      .join("|")
  } else if (request.salesman) {
    searchParams = omit(searchIdExcludedParams.salesman, {
      ...defaultSearchParams,
      ...createSearchParams(request),
      salesman: request.salesman,
      flight_type: "salesman",
    })
    searchId = [
      (searchParams.salesman.via || [])
        .map(stop =>
          [
            stop.locations.join("|"),
            stop.nights_range ? stop.nights_range.join("|") : "",
            stop.date_range ? stop.date_range.join("|") : "",
          ].join("|"),
        )
        .join("|"),
      Object.keys(searchParams)
        .filter(
          key =>
            key !== "salesman" &&
            (!searchIdExcludedParams[request.featureName] ||
              !searchIdExcludedParams[request.featureName].includes(key)),
        )
        .map(key => `${key}=${searchParams[key]}`)
        .join("&"),
    ].join("|")
  } else {
    searchParams = {
      ...defaultSearchParams,
      ...createSearchParams(request),
    }
    searchId = Object.keys(searchParams)
      .filter(
        key =>
          !searchIdExcludedParams[request.featureName] ||
          !searchIdExcludedParams[request.featureName].includes(key),
      )
      .map(key => `${key}=${searchParams[key]}`)
      .join("&")
  }
  // it identifies this search - similar as URL and content in POST
  searchId = `${skypickerApiUrl}@${searchId}`

  return { searchParams, skypickerApiUrl, umbrellaUrl, searchId }
}

export const mapParams = (params, limit, window) => ({
  ...params,
  ...(limit && !params.one_per_date && { limit, offset: 0 }),
  ...(window && { device: window }),
})

export const mapParamsMulticity = (params, limit) => ({
  v: 3,
  curr: "EUR",
  partner_market: params.locale,
  ...pickMulticityParams(params),
  ...(limit && !params.one_per_date ? { limit, offset: 0 } : null),
  // ...(params.airlinesList && {
  //   select_airlines: params.airlinesList,
  //   select_airlines_exclude: params.selectedAirlinesExclude ? "True" : "False",
  // }),
  // ...(params.airportsList && {
  //   select_stop_airport: params.airportsList,
  //   select_stop_airport_exclude: params.selectedStopoverAirportsExclude ? "True" : "False",
  // }),
})

export function makeUrl(url, params, limit, mapParamsFn = mapParams) {
  const paramsMapped = mapParamsFn(params, limit)

  return `${url}${makeQuery(paramsMapped)}`
}

export function fetchFlights({ searchParams, skypickerApiUrl }, limit = null) {
  const customSearchAPIHeaders =
    searchParams.featureName === FEATURE_NAMES.MAP
      ? {
          "X-API-VERSION": 3,
        }
      : {}

  let promise
  if (searchParams.multicity) {
    // multicity
    promise = fetch(
      makeUrl(skypickerApiUrl, searchParams.multicity[0], limit, mapParamsMulticity),
      {
        method: "post",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...commonSearchAPIHeaders,
          ...customSearchAPIHeaders,
        },
        body: JSON.stringify({
          via_blocks: searchParams.multicity.map(trip => ({
            fly_from: trip.flyFrom.split(","),
            fly_to: trip.to.split(","),
            max_stopovers:
              trip.maxstopovers === undefined ? undefined : parseInt(trip.maxstopovers, 10),
            dep_date_range: [trip.dateFrom, trip.dateTo],
          })),
        }),
      },
    )
  } else if (searchParams.salesman) {
    promise = fetch(makeUrl(skypickerApiUrl, omit(["salesman"], searchParams), limit), {
      method: "post",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...commonSearchAPIHeaders,
        ...customSearchAPIHeaders,
      },
      body: JSON.stringify(searchParams.salesman),
    })
  } else {
    promise = fetch(makeUrl(skypickerApiUrl, searchParams, limit), {
      method: "get",
      headers: {
        Accept: "application/json",
        ...commonSearchAPIHeaders,
        ...customSearchAPIHeaders,
      },
    })
  }

  const startTime = new Date().getTime()
  // log in parallel
  const params = mapParams(searchParams, limit)
  promise
    // Disable search logging, until we solve capacity on Loggly
    // .then(() => {
    //   const endTime = new Date().getTime()
    //   const duration = endTime - startTime
    //   cuckoo.track(cuckooEvents.SEARCH_FLIGHTS_API_SUCCESS, {
    //     duration,
    //     url: skypickerApiUrl,
    //     query: params,
    //   })
    // })
    .catch(error => {
      const endTime = new Date().getTime()
      const duration = endTime - startTime
      cuckoo.track(cuckooEvents.SEARCH_FLIGHTS_API_ERROR, { error, query: params, duration })
    })
  return promise
    .then(res => {
      if (!res.ok) {
        throw Error(res.status)
      }
      return res.json()
    })
    .then(jsonData => {
      // salesman errors return 200 ok with error message in JSON
      if (jsonData.detailed) {
        throw Error(jsonData.detailed.message)
      }
      return (
        jsonData && {
          data: jsonData.data || jsonData, // response from `flights_multi` is just an array of flights
          allStopoverAirports: jsonData.all_stopover_airports,
          allAirlines: jsonData.all_airlines,
          locationHashtags: jsonData.location_hashtags,
          _next: jsonData._next === undefined ? undefined : Boolean(jsonData._next),
        }
      )
    })
}
